import type { SiteSection } from '../domains/site/schema';
import ctaSectionSource from '../components/sections/cta-section.tsx?raw';
import faqSectionSource from '../components/sections/faq-section.tsx?raw';
import featureListSectionSource from '../components/sections/feature-list-section.tsx?raw';
import featuresSectionSource from '../components/sections/features-section.tsx?raw';
import footerSectionSource from '../components/sections/footer-section.tsx?raw';
import heroSectionSource from '../components/sections/hero-section.tsx?raw';
import pricingSectionSource from '../components/sections/pricing-section.tsx?raw';
import proofSectionSource from '../components/sections/proof-section.tsx?raw';
import reviewSectionSource from '../components/sections/review-section.tsx?raw';
import servicesSectionSource from '../components/sections/services-section.tsx?raw';
import stepsSectionSource from '../components/sections/steps-section.tsx?raw';
import themePresentationSource from '../lib/theme-presentation.ts?raw';
import cnSource from '../utils/cn.ts?raw';

export type SectionTemplateDefinition = {
  filePath: string;
  source: string;
};

const sectionSourceSnapshots = import.meta.glob('/src/components/sections/**/*.{ts,tsx}', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

const workspaceSourceSnapshots: Record<string, string> = {
  ...sectionSourceSnapshots,
  '/src/lib/theme-presentation.ts': themePresentationSource,
  '/src/utils/cn.ts': cnSource,
};

const staticImportPattern =
  /(?:import|export)\s+(?:type\s+)?(?:[^'"]*?\s+from\s+)?["']([^"']+)["']/gu;
const dynamicImportPattern = /import\(\s*["']([^"']+)["']\s*\)/gu;

const featureListSectionTypes = new Set([
  'feature-list',
  'problem',
  'solution',
  'benefit',
  'feature_list',
  'reasons',
  'use_cases',
  'comparison',
  'people',
  'message',
  'target_audience',
  'overview',
  'speaker',
  'instructor',
  'work_style',
  'course_or_curriculum',
  'offer',
  'access',
]);

function normalizeWorkspaceSourcePath(path: string) {
  const normalizedParts: string[] = [];

  for (const segment of path.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') {
      normalizedParts.pop();
      continue;
    }
    normalizedParts.push(segment);
  }

  return normalizedParts.join('/');
}

function getWorkspaceSourceDirectory(path: string) {
  const slashIndex = path.lastIndexOf('/');
  return slashIndex >= 0 ? path.slice(0, slashIndex) : '';
}

function toWorkspaceSnapshotPath(path: string) {
  return path.startsWith('/') ? path : `/${path}`;
}

function buildWorkspaceImportCandidates(fromFilePath: string, specifier: string) {
  const normalizedSpecifier = specifier.startsWith('/')
    ? specifier.slice(1)
    : normalizeWorkspaceSourcePath(
      getWorkspaceSourceDirectory(fromFilePath)
        ? `${getWorkspaceSourceDirectory(fromFilePath)}/${specifier}`
        : specifier,
    );

  return [
    normalizedSpecifier,
    `${normalizedSpecifier}.ts`,
    `${normalizedSpecifier}.tsx`,
    `${normalizedSpecifier}.json`,
    `${normalizedSpecifier}/index.ts`,
    `${normalizedSpecifier}/index.tsx`,
    `${normalizedSpecifier}/index.json`,
  ];
}

function parseWorkspaceLocalImportSpecifiers(source: string) {
  const specifiers: string[] = [];
  const seen = new Set<string>();

  for (const pattern of [staticImportPattern, dynamicImportPattern]) {
    pattern.lastIndex = 0;

    for (const match of source.matchAll(pattern)) {
      const specifier = match[1]?.trim();
      if (!specifier?.startsWith('.')) continue;
      if (seen.has(specifier)) continue;
      seen.add(specifier);
      specifiers.push(specifier);
    }
  }

  return specifiers;
}

function resolveWorkspaceDependencyFilePath(fromFilePath: string, specifier: string) {
  return buildWorkspaceImportCandidates(fromFilePath, specifier)
    .map((candidate) => toWorkspaceSnapshotPath(candidate))
    .find((candidate) => Boolean(workspaceSourceSnapshots[candidate]))
    ?.slice(1)
    ?? null;
}

function inferWorkspaceFileLanguage(filePath: string): 'ts' | 'tsx' | 'json' {
  if (filePath.endsWith('.tsx')) return 'tsx';
  if (filePath.endsWith('.json')) return 'json';
  return 'ts';
}

export function getSectionTemplateDefinition(sectionType: SiteSection['type']): SectionTemplateDefinition | null {
  if (featureListSectionTypes.has(sectionType)) {
    return {
      filePath: 'src/components/sections/feature-list-section.tsx',
      source: featureListSectionSource,
    };
  }

  switch (sectionType) {
    case 'hero':
      return { filePath: 'src/components/sections/hero-section.tsx', source: heroSectionSource };
    case 'services':
      return { filePath: 'src/components/sections/services-section.tsx', source: servicesSectionSource };
    case 'features':
      return { filePath: 'src/components/sections/features-section.tsx', source: featuresSectionSource };
    case 'proof':
      return { filePath: 'src/components/sections/proof-section.tsx', source: proofSectionSource };
    case 'review':
      return { filePath: 'src/components/sections/review-section.tsx', source: reviewSectionSource };
    case 'pricing':
      return { filePath: 'src/components/sections/pricing-section.tsx', source: pricingSectionSource };
    case 'steps':
      return { filePath: 'src/components/sections/steps-section.tsx', source: stepsSectionSource };
    case 'faq':
      return { filePath: 'src/components/sections/faq-section.tsx', source: faqSectionSource };
    case 'cta':
      return { filePath: 'src/components/sections/cta-section.tsx', source: ctaSectionSource };
    case 'footer':
      return { filePath: 'src/components/sections/footer-section.tsx', source: footerSectionSource };
    default:
      return null;
  }
}

export function getWorkspaceSourceSnapshot(filePath: string): string | null {
  return workspaceSourceSnapshots[toWorkspaceSnapshotPath(filePath)] ?? null;
}

export function collectWorkspaceSourceDependencyFiles(
  entryFilePaths: readonly string[],
): Array<{
  path: string;
  language: 'ts' | 'tsx' | 'json';
  source: string;
}> {
  const queue = [...entryFilePaths];
  const visited = new Set<string>();
  const resolvedFiles: Array<{
    path: string;
    language: 'ts' | 'tsx' | 'json';
    source: string;
  }> = [];

  while (queue.length > 0) {
    const currentFilePath = queue.shift();
    if (!currentFilePath || visited.has(currentFilePath)) {
      continue;
    }

    const source = getWorkspaceSourceSnapshot(currentFilePath);
    if (!source) {
      continue;
    }

    visited.add(currentFilePath);
    resolvedFiles.push({
      path: currentFilePath,
      language: inferWorkspaceFileLanguage(currentFilePath),
      source,
    });

    for (const specifier of parseWorkspaceLocalImportSpecifiers(source)) {
      const dependencyFilePath = resolveWorkspaceDependencyFilePath(currentFilePath, specifier);
      if (!dependencyFilePath || visited.has(dependencyFilePath)) {
        continue;
      }
      queue.push(dependencyFilePath);
    }
  }

  return resolvedFiles.sort((left, right) => left.path.localeCompare(right.path));
}

export const sharedWorkspaceCodeFirstEntryFilePaths = [
  'src/components/sections/site-section-renderer.tsx',
  'src/components/sections/index.ts',
] as const;
